import { Button } from "@/components/ui/Button";
import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Divider } from "@mui/material";
import { useToastContext } from "@/context/ToastContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import ConnectionInfo from "@/components/ConnectionInfo";
import { notFoundUrl } from "@/routes/urls";
import { isValidGUID } from "@/utils/validation";
import { CreateLiquidityScenarioDialog } from "@/components/liquidity/CreateLiquidityScenarioDialog";
import { useCreateLiquidityScenario, useDeleteLiquidityScenario, useLiquidityScenario, useLiquidityScenarioHeads, useUpdateLiquidityScenarioHead } from "@/hooks/use-liquidity-scenario";
import { NewLiquidityScenario, TransactionType } from "@/types/liquidity-api";
import { LiquidityScenarioCard } from "@/components/liquidity/LiquidityScenarioCard";
import LiquidityTransactionsTable from "@/components/liquidity/LiquidityTransactionsTable";
import { useConnection } from "@/hooks/use-connection";
import { LiquidityGraph } from "@/components/liquidity/LiquidityGraph";
import { LiquiditySummary } from "@/components/liquidity/LiquiditySummary";
import LiquidityBalanceTable from "@/components/liquidity/LiquidityBalanceTable";
import { Tabs, Tab } from "@mui/material";
import Background from "@/components/ui/Background";

interface ToastHelper {
  isSuccess?: boolean;
  error?: Error;
  origin: string;
  message: string;
}

const LiquidityModule = () => {
  const navigate = useNavigate();
  const { showToast } = useToastContext();
  const { clientId, connectionId } = useParams<{ clientId: string; connectionId: string }>();

  useEffect(() => {
    if (!clientId || !connectionId || !isValidGUID(clientId) || !isValidGUID(connectionId)) {
      navigate(notFoundUrl);
    }
  }, [navigate, clientId, connectionId]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedLiqidityScenarioId, setSelectedLiquidityScenarioId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('graph');

  // Hooks
  const { selectedLiquidityScenario, isLoadingScenario, scenarioError } = useLiquidityScenario(selectedLiqidityScenarioId);
  const { liquidityScenarioHeads, isLoadingLiquidityScenarioHeads: isLoadingScenarioHeads, liquidityScenarioHeadsError: scenarioHeadsError } = useLiquidityScenarioHeads(clientId, connectionId);
  const { createNewLiquidityScenario, isCreating, createSuccess, createError } = useCreateLiquidityScenario(clientId, connectionId);
  const { deleteLiquidityScenario, isDeleting, deleteSuccess, deleteError } = useDeleteLiquidityScenario(clientId, connectionId);
  const { updateLiquidityScenario, isUpdating, updateSuccess, updateError } = useUpdateLiquidityScenarioHead(selectedLiqidityScenarioId);
  const { isLoadingConnection } = useConnection(clientId!, connectionId!);

  const isLoadingMainContent = isLoadingScenario || isDeleting;

  const handleDeleteScenario = (scenarioId: string) => {
    deleteLiquidityScenario(scenarioId);
    setSelectedLiquidityScenarioId(null);
  }

  const previousStatesRef = useRef({
    create: isCreating,
    update: isUpdating,
    delete: isDeleting,
    loadScenario: isLoadingScenario,
    loadScenarioHeads: isLoadingScenarioHeads
  });

  // Use memo to avoid re-rendering the toast on every render
  const errorSource: ToastHelper | null = useMemo(() => {
    const item = [
      { error: createError, wasPreviouslyPending: previousStatesRef.current.create, origin: 'create', message: 'Failed to create scenario' },
      { error: updateError, wasPreviouslyPending: previousStatesRef.current.update, origin: 'update', message: 'Failed to update scenario' },
      { error: deleteError, wasPreviouslyPending: previousStatesRef.current.delete, origin: 'delete', message: 'Failed to delete scenario' },
      { error: scenarioError, wasPreviouslyPending: previousStatesRef.current.loadScenario, origin: 'scenario', message: 'Failed to load scenario' },
      { error: scenarioHeadsError, wasPreviouslyPending: previousStatesRef.current.loadScenarioHeads, origin: 'scenario-heads', message: 'Failed to load scenario heads' }
    ].find(({ error, wasPreviouslyPending }) => !!error && wasPreviouslyPending)
    return item;
  }, [createError, updateError, deleteError, scenarioError, scenarioHeadsError]);

  // Use memo to avoid re-rendering the toast on every render
  const successSource: ToastHelper | null = useMemo(() => {

    const successItem = [
      { isSuccess: createSuccess, wasPreviouslyPending: previousStatesRef.current.create, origin: 'create', message: 'Scenario created successfully' },
      { isSuccess: updateSuccess, wasPreviouslyPending: previousStatesRef.current.update, origin: 'update', message: 'Scenario updated successfully' },
      { isSuccess: deleteSuccess, wasPreviouslyPending: previousStatesRef.current.delete, origin: 'delete', message: 'Scenario deleted successfully' }
    ].find(({ isSuccess, wasPreviouslyPending }) => isSuccess && wasPreviouslyPending);

    return successItem;
  }, [createSuccess, updateSuccess, deleteSuccess]);

  useEffect(() => {
    // Update the previous states for the next render
    previousStatesRef.current = {
      create: isCreating,
      update: isUpdating,
      delete: isDeleting,
      loadScenario: isLoadingScenario,
      loadScenarioHeads: isLoadingScenarioHeads
    };
  }, [isCreating, isUpdating, isDeleting, isLoadingScenario, isLoadingScenarioHeads]);

  useEffect(() => {
    if (successSource) {
      showToast({
        title: "Success",
        description: successSource.message,
        variant: "success",
      });
    } else if (errorSource) {
      showToast({
        title: "Error",
        description: errorSource.message,
        variant: "error",
      });
    }
  }, [errorSource, successSource, showToast]);


  const handleCreateLiquidityScenario = async (
    name: string,
    dateFrom: Date,
    dateTo: Date,
    includeCustomerTransactions: boolean,
    includeSupplierTransactions: boolean
  ) => {
    const includedTransactionTypes: Array<TransactionType> = [
      ...(includeCustomerTransactions ? [TransactionType.Invoice] : []),
      ...(includeSupplierTransactions ? [TransactionType.SupplierInvoice] : [])
    ];

    const liquidityScenario: NewLiquidityScenario = {
      clientId,
      connectionIds: [connectionId],
      name,
      dateFrom,
      dateTo,
      includedTransactionTypes,
    }
    createNewLiquidityScenario(liquidityScenario);
  };

  return isLoadingScenarioHeads || isLoadingConnection ? (
    <div className="fixed inset-0 flex justify-center items-center z-50 bg-white/75">
      <LoadingSpinner message="Loading liquidity scenarios..." />
    </div>
  ) : (
    <Background className="px-4 py-4">
      <div className="flex items-center mb-4 mt-2 justify-between h-12">
        <h1 className="text-3xl font-bold">Liquidity Scenarios</h1>
        <div className="flex justify-end items-center gap-4">

          <ConnectionInfo />
          <Button
            size="large"
            color="primary"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={isCreating}
            className="flex gap-2"
          >
            <Plus className="h-4 w-4" />
            {"Create Scenario"}
          </Button>
        </div>
      </div>

      <div className="h-4" />

      {liquidityScenarioHeads && (
        <div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {liquidityScenarioHeads?.map((scenario) => (
              <LiquidityScenarioCard
                key={scenario.id}
                scenarioHead={scenario}
                isDeleting={false}
                isSelected={selectedLiqidityScenarioId === scenario.id}
                onDelete={() => handleDeleteScenario(scenario.id)}
                onSelect={() => {
                  setSelectedLiquidityScenarioId(scenario.id);
                  console.log('Selected Scenario:', selectedLiqidityScenarioId);
                }}
                onUpdateName={(newName) => {
                  updateLiquidityScenario({ liquidityScenarioId: scenario.id, data: { name: newName, openingBalance: 0 } }); // FIXME: Set proper opening balance, should be included in the ScenarioHead
                }}
              />
            ))}
          </div>
          <div className="h-10" />
        </div>
      )}

      {
        isCreating && (
          <div className="fixed inset-0 flex justify-center items-center z-50 bg-white/75">
            <LoadingSpinner message={"Creating scenario, this might take few minutes..."} />
          </div>
        )
      }

      {selectedLiqidityScenarioId && (
        isLoadingMainContent ? (
          <div className="fixed inset-0 flex justify-center items-center z-50 bg-white/75">
            <LoadingSpinner message="Loading scenario..." />
          </div>
        ) : (
          <div>
            <Divider />

            <div className="h-10" />

            <LiquiditySummary scenario={selectedLiquidityScenario} updateLiquidityScenario={updateLiquidityScenario} />

            <div className="h-6" />

            <div className="border border-gray-300 rounded-md bg-background-light">
              <Tabs variant="fullWidth" value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab
                  label="Graf"
                  value="graph"
                  className={`border border-gray-300 rounded-md mx-1 ${activeTab === 'graph' ? '' : 'hover:bg-gray-100'}`} />
                <Tab
                  label="Tabell"
                  value="table"
                  className={`border border-gray-300 rounded-md mx-1 ${activeTab === 'balance' ? '' : 'hover:bg-gray-100'}`} />
              </Tabs>
              {activeTab === 'graph' && (
                <LiquidityGraph scenario={selectedLiquidityScenario} />
              )}
              {activeTab === 'table' && (
                <LiquidityBalanceTable scenario={selectedLiquidityScenario} />
              )}
            </div>


            <div className="h-6" />

            <LiquidityTransactionsTable
              selectedLiquidityScenario={selectedLiquidityScenario}
            />
          </div>
        )
      )}

      <CreateLiquidityScenarioDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreateLiquidityScenario={handleCreateLiquidityScenario}
      />

    </Background >
  );
};

export default LiquidityModule;
